import { ageOn, CHILD_AGE_LIMIT, CHILD_REGISTER_LIMIT, MAX_CHILDREN, type ConsentState, type ConsentType } from '@selfon/domain/browser';
import { useEffect, useState } from 'react';
import { api, type ChildRow, type SharedNoteView } from '../api';
import { StudentPassPay } from '../components/CardPayment';
import { useAuth } from '../auth/AuthContext';
import { Alert, Chip, Dialog, Empty, errorText, Field, Loading, PageHeader, useLoad } from '../components/ui';
import { dateOnly, todayKst, won } from '../format';

/** 오늘 기준 만 19세 미만이 되는 가장 이른 생일(날짜 입력칸의 최소값) */
function earliestBirth(today: string) {
  const [y, m, d] = today.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y - CHILD_REGISTER_LIMIT, m - 1, d + 1));
  return dt.toISOString().slice(0, 10);
}

/** 자녀 한 명의 동의 관리. 바꾼 항목만 저장하고, 동의·철회 기록은 센터 감사 로그에도 남는다. */
function ConsentDialog({ child, onClose, onSaved }: { child: ChildRow | null; onClose: () => void; onSaved: (msg: string) => void }) {
  const [items, setItems] = useState<ConsentState[] | null>(null);
  const [draft, setDraft] = useState<Partial<Record<ConsentType, boolean>>>({});
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!child) return;
    setItems(null); setDraft({}); setError(null);
    api.childConsents(child.id).then((r) => setItems(r.items), (e) => setError(errorText(e)));
  }, [child]);
  const value = (i: ConsentState) => draft[i.type] ?? i.granted;
  const changed = (items ?? []).filter((i) => value(i) !== i.granted);
  const serviceOff = items?.some((i) => i.type === 'SERVICE' && !value(i));

  return (
    <Dialog title={`${child?.name ?? ''} 동의 관리`} open={Boolean(child)} onClose={onClose} submitLabel="저장"
      onSubmit={async () => {
        for (const i of changed) await api.setChildConsent(child!.id, i.type, value(i));
        onSaved(changed.length ? `${child!.name}의 동의 ${changed.length}건을 저장했습니다.` : '바뀐 내용이 없습니다.');
      }}>
      <p className="muted">언제든 바꿀 수 있고, 바꾼 기록은 남습니다.</p>
      {error && <Alert>{error}</Alert>}
      {!items && !error && <Loading />}
      {items && (
        <ul className="plain consent-list">
          {items.map((i) => (
            <li key={i.type}>
              <label className="consent-item">
                <input type="checkbox" checked={value(i)} onChange={(e) => setDraft({ ...draft, [i.type]: e.target.checked })} />
                <span>
                  <strong>{i.title}</strong>
                  <span className="muted small">{i.desc}</span>
                  {i.changedAt && <span className="muted small">마지막 변경 {i.changedAt.slice(0, 10)}</span>}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
      {serviceOff && <Alert kind="info">철회하면 센터가 일정·출결·과제를 처리할 수 없습니다. 센터와 먼저 상의해 주세요.</Alert>}
    </Dialog>
  );
}

/** 자녀가 자기 계정으로 가입했다면 코드로 이어 결제를 맡는다. 학생에게 카드 정보는 보이지 않는다. */
function PayerSection({ onMessage }: { onMessage: (m: string) => void }) {
  const q = useLoad(() => api.payerStudents(), []);
  const [invite, setInvite] = useState<{ code: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState<{ studentId: string; studentName: string } | null>(null);
  const issue = () => { setError(null); api.newPayerInvite().then((r) => setInvite(r.invite), (e) => setError(errorText(e))); };
  const end = (id: string, name: string) => { setError(null); api.endPayerLink(id).then(() => { onMessage(`${name}님과의 결제 연결을 해제했습니다.`); q.reload(); }, (e) => setError(errorText(e))); };
  return (
    <section className="card stack" aria-labelledby="payer-section">
      <h2 id="payer-section">학생 계정 결제 연결</h2>
      <p className="muted small">자녀가 일반인으로 직접 가입했다면, 코드를 건네 보호자가 결제를 맡을 수 있습니다. 결제는 언제나 보호자 계정에서 직접 합니다.</p>
      {(q.error || error) && <Alert>{q.error ?? error}</Alert>}
      {q.data?.students.length ? (
        <ul className="plain stack">
          {q.data.students.map((s) => (
            <li key={s.studentId} className="payer-row">
              <span>{s.studentName} <span className="muted small">{s.passUntil ? `이용권 ${dateOnly(s.passUntil)}까지` : '이용권 없음'}</span>{s.passRequestedAt && <Chip tone="warn">결제 요청</Chip>}</span>
              <span className="row-actions">
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setPaying(s)}>월 이용권 결제 {won(q.data!.price)}</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => end(s.studentId, s.studentName)}>연결 해제</button>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {invite && <p>연결 코드 <strong className="mono">{invite.code}</strong> <span className="muted small">({invite.expiresAt.slice(0, 10)}까지, 한 번만 쓸 수 있어요)</span></p>}
      <button type="button" className="btn btn-outline" onClick={issue}>연결 코드 만들기</button>
      <StudentPassPay student={paying} price={q.data?.price ?? 0} onClose={() => setPaying(null)} onDone={(m) => { onMessage(m); q.reload(); }} />
    </section>
  );
}

/**
 * 보호자: 자녀 등록(만 19세 미만, 4명까지)과 자녀 모드.
 * 자녀 모드에서는 아이가 학생 화면(과제)만 쓰고, 보호자 화면으로 돌아오려면 로그아웃 후 보호자 비밀번호로 다시 로그인해야 한다.
 */
export function Family() {
  const { me, startChildMode } = useAuth();
  const q = useLoad(() => api.children(), []);
  const [form, setForm] = useState({ name: '', birthDate: '', teenAgreed: false });
  const [adding, setAdding] = useState(false);
  const [modeFor, setModeFor] = useState<ChildRow | null>(null);
  const [removing, setRemoving] = useState<ChildRow | null>(null);
  const [consentFor, setConsentFor] = useState<ChildRow | null>(null);
  const [linkFor, setLinkFor] = useState<ChildRow | null>(null);
  const [code, setCode] = useState('');
  const [shared,setShared]=useState<{name:string;notes:SharedNoteView[]}|null>(null);
  const [sharedError,setSharedError]=useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const today = todayKst();
  const list = q.data?.children ?? [];
  const full = list.length >= MAX_CHILDREN;
  const teen = /^\d{4}-\d{2}-\d{2}$/.test(form.birthDate) && ageOn(form.birthDate, today) >= CHILD_AGE_LIMIT;

  return (
    <>
      <PageHeader title="우리 아이"
        actions={<button type="button" className="btn btn-primary" disabled={full || !q.data} onClick={() => { setForm({ name: '', birthDate: '', teenAgreed: false }); setAdding(true); }}>＋ 자녀 등록 ({list.length}/{MAX_CHILDREN})</button>} />
      {msg && <Alert kind="ok">{msg}</Alert>}
      {q.error && <Alert>{q.error}</Alert>}
      {q.loading && !q.data && <Loading />}
      {full && <Alert kind="info">자녀는 {MAX_CHILDREN}명까지 등록할 수 있습니다.</Alert>}
      {q.data && list.length === 0 && <Empty>{me?.name ? `${me.name}님, ` : ''}아직 등록한 자녀가 없습니다. 오른쪽 위 ‘자녀 등록’으로 시작하세요.</Empty>}

      <ul className="child-cards">
        {list.map((c) => (
          <li key={c.id} className="child-card">
            <div className="child-avatar" aria-hidden="true">{c.name.slice(0, 1)}</div>
            <div className="child-info">
              <h2>{c.name}</h2>
              <p className="muted small">만 {ageOn(c.birthDate, today)}세 · {c.birthDate}</p>
              {c.link ? <Chip tone="ok">{c.centerName ?? '센터'} 연결됨</Chip> : <Chip tone="muted">센터 미연결</Chip>}
              
            </div>
            <div className="child-actions">
              <button type="button" className="btn btn-primary" onClick={() => setModeFor(c)}>자녀 모드 시작</button>
              {c.link&&<button type="button" className="btn btn-outline" onClick={async()=>{setSharedError('');try{setShared({name:c.name,notes:(await api.childSharedNotes(c.id)).notes});}catch(e){setSharedError(errorText(e));}}}>회기 안내 보기</button>}
              {c.link && <button type="button" className="btn btn-outline" onClick={() => setConsentFor(c)}>동의 관리</button>}
              {!c.link && <button type="button" className="btn btn-outline" onClick={() => { setCode(''); setLinkFor(c); }}>센터 연결</button>}
              {!c.link && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRemoving(c)}>등록 해제</button>}
            </div>
          </li>
        ))}
      </ul>

      {sharedError&&<Alert>{sharedError}</Alert>}
      {shared&&<section className="card stack"><h2>{shared.name} 회기 안내</h2>{!shared.notes.length&&<p>선생님이 공유한 회기 안내가 없습니다.</p>}{shared.notes.map(n=><article key={n.sessionId}><h3>{n.sessionStartAt.slice(0,10)}</h3><p style={{whiteSpace:'pre-wrap'}}>{n.message}</p></article>)}</section>}
      <Dialog title="자녀 등록" open={adding} onClose={() => setAdding(false)} submitLabel="등록"
        onSubmit={async () => { const r = await api.addChild({ name: form.name, birthDate: form.birthDate, teenAgreed: teen ? form.teenAgreed : undefined }); setMsg(`${r.child.name}을(를) 자녀로 등록했습니다.`); q.reload(); }}>
        <Field id="ch-name" label="자녀 이름"><input id="ch-name" required maxLength={20} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field id="ch-birth" label="생년월일" hint={`만 ${CHILD_REGISTER_LIMIT}세 미만만 등록할 수 있습니다(${earliestBirth(today)} 이후 출생).`}>
          <input id="ch-birth" type="date" required min={earliestBirth(today)} max={today} value={form.birthDate} onChange={(e) => setForm({ ...form, birthDate: e.target.value })} aria-describedby="ch-birth-hint" />
        </Field>
        {teen && (
          <label className="consent-item">
            <input type="checkbox" required checked={form.teenAgreed} onChange={(e) => setForm({ ...form, teenAgreed: e.target.checked })} />
            <span>자녀 본인이 이 등록에 동의했습니다.<span className="muted small">만 {CHILD_AGE_LIMIT}세 이상은 본인 뜻이 중요합니다. 자녀가 직접 일반인으로 가입할 수도 있습니다.</span></span>
          </label>
        )}
        
      </Dialog>

      <PayerSection onMessage={setMsg} />

      <Dialog title={`${modeFor?.name ?? ''} 자녀 모드 시작`} open={Boolean(modeFor)} onClose={() => setModeFor(null)} submitLabel="자녀 모드 시작"
        onSubmit={async () => { await startChildMode(modeFor!.id); }}>
        <ul className="plain stack">
          <li>이 기기가 <strong>{modeFor?.name}</strong>의 학생 화면(오늘의 과제)으로 바뀝니다.</li>
          <li>자녀 모드에서는 보호자 메뉴·자녀 관리가 보이지 않습니다.</li>
          <li><strong>자녀 모드를 끄려면 로그아웃한 뒤 보호자 이메일·비밀번호로 다시 로그인</strong>해야 합니다. 새로고침해도 자녀 모드가 유지됩니다.</li>
        </ul>
      </Dialog>

      <Dialog title={`${linkFor?.name ?? ''} 센터 연결`} open={Boolean(linkFor)} onClose={() => setLinkFor(null)} submitLabel="연결"
        onSubmit={async () => { const r = await api.linkChild(linkFor!.id, code); setMsg(`${r.child.name}을(를) ${r.child.centerName ?? '센터'}와 연결했습니다. 동의 관리에서 동의 항목을 확인해 주세요.`); q.reload(); }}>
        <Field id="ln-code" label="연결 코드" hint="센터에서 받은 8자리 코드">
          <input id="ln-code" required autoComplete="off" autoCapitalize="characters" spellCheck={false} maxLength={12} className="mono" value={code} onChange={(e) => setCode(e.target.value)} aria-describedby="ln-code-hint" />
        </Field>
        
      </Dialog>

      <ConsentDialog child={consentFor} onClose={() => setConsentFor(null)} onSaved={setMsg} />

      <Dialog danger title="자녀 등록 해제" open={Boolean(removing)} onClose={() => setRemoving(null)} submitLabel="등록 해제"
        onSubmit={async () => { await api.removeChild(removing!.id); setMsg(`${removing!.name}의 등록을 해제했습니다.`); q.reload(); }}>
        <p>{removing?.name}의 자녀 정보를 지웁니다. 센터와 연결되지 않은 자녀만 지울 수 있습니다.</p>
      </Dialog>
    </>
  );
}
